import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { Capacitor } from '@capacitor/core';

import { useDentist } from './DentistContext';
import { useAuth } from '@/contexts/AuthenticationContext';
import { useClinic } from '@/contexts/ClinicContext';
import { useNetwork } from '@/contexts/NetworkContext';
import { useToast } from '@/hooks';
import { PLATFORMS } from '@/utils/constants';
import { parseDate } from '@/utils/helpers/parseDate';
import {
  ENTITLEMENT_IDENTIFIERS,
  PLAN_LIMITS,
  PROFILE_COUNT_LIMITS
} from '@/utils/subscriptions';
import {
  LOG_LEVEL,
  Purchases,
  PurchasesEntitlementInfo,
  PurchasesEntitlementInfos,
  PurchasesPackage,
  VERIFICATION_RESULT
} from '@revenuecat/purchases-capacitor';
import { RevenueCatUI } from '@revenuecat/purchases-capacitor-ui';
import { ActiveSubscriptionsCounter } from 'capacitor-active-subscriptions-counter';
import { isAfter } from 'date-fns';

interface SubscriptionContextType {
  loading: boolean;
  loaded: boolean;
  purchasing: boolean;
  isSubscribed: boolean | undefined;
  checkEligibilityForTrial: () => Promise<void>;
  isEligibleForTrial: boolean | undefined;
  login: (userId: string) => Promise<void>;
  logout: () => Promise<void>;
  plans: PurchasesPackage[];
  selectedPlan: PurchasesPackage | undefined;
  setSelectedPlan: (plan: PurchasesPackage) => void;
  purchase: () => Promise<void>;
  restore: () => Promise<void>;
  profileCountLimit: number | undefined;
  entitlement: PurchasesEntitlementInfo | undefined;
  manage: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children
}) => {
  const displayToast = useToast();
  const { authenticated } = useAuth();
  const { isClinic, selectedClinic: clinic } = useClinic();
  const { dentists } = useDentist();
  const { connected } = useNetwork();
  const [loading, setLoading] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [purchasing, setPurchasing] = useState<boolean>(false);
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean | undefined>(
    undefined
  );
  const [isEligibleForTrial, setIsEligibleForTrial] = useState<
    boolean | undefined
  >(undefined);
  const [plans, setPlans] = useState<PurchasesPackage[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<
    PurchasesPackage | undefined
  >(undefined);
  const [entitlements, setEntitlements] = useState<PurchasesEntitlementInfos>({
    all: {},
    active: {},
    verification: 'NOT_REQUESTED' as VERIFICATION_RESULT
  });
  const [customerInfoListener, setCustomerInfoListener] = useState<
    string | undefined
  >(undefined);
  const platform = Capacitor.getPlatform();

  const handleError = (message: string, showMessage: boolean = false) => {
    if (showMessage) {
      displayToast({
        message,
        position: 'bottom'
      });
    }
  };

  const configure = useCallback(async () => {
    if (!connected || isConfigured || platform === PLATFORMS.WEB) {
      return;
    }

    try {
      await Purchases.setLogLevel({
        level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO
      });
      await Purchases.configure({
        apiKey:
          platform === PLATFORMS.IOS
            ? import.meta.env.VITE_REVENUE_CAT_IOS_KEY
            : import.meta.env.VITE_REVENUE_CAT_ANDROID_KEY
      });
      setIsConfigured(true);
    } catch {
      handleError('Error configuring RevenueCat.');
    }
  }, [connected, platform, isConfigured]);

  const checkEligibilityForTrial = useCallback(async () => {
    if (platform === PLATFORMS.WEB || !connected || !isConfigured) {
      return;
    }

    const localSubscriptionsCount = Object.keys(entitlements.all).length;

    /**
     * First time users are eligible for a 1 week free trial
     * Try using both local and global subscriptions count
     * Fallback to local subscriptions count
     */
    try {
      const { count: globalSubscriptionsCount } =
        await ActiveSubscriptionsCounter.getActiveSubscriptionsCount();

      setIsEligibleForTrial(
        localSubscriptionsCount === 0 && globalSubscriptionsCount === 0
      );
    } catch {
      setIsEligibleForTrial(localSubscriptionsCount === 0);
    }
  }, [connected, isConfigured, platform, entitlements.all]);

  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      const { entitlements: fetchedEntitlements } = customerInfo;
      if (fetchedEntitlements) {
        setIsSubscribed(Object.keys(fetchedEntitlements.active).length > 0);
        setEntitlements(fetchedEntitlements);
      }

      const listener = await Purchases.addCustomerInfoUpdateListener(
        ({ entitlements: updatedEntitlements }) => {
          if (updatedEntitlements) {
            setIsSubscribed(Object.keys(updatedEntitlements.active).length > 0);
            setEntitlements(updatedEntitlements);
          }
        }
      );

      setCustomerInfoListener(listener);
    } catch {
      handleError('Error checking subscription status.');
    }
  }, []);

  /**
   * Checks how many active subscriptions exist on the user's Apple or Google account,
   * regardless of the current RevenueCat customer. This is used to determine
   * if the user can start a new subscription (e.g., for an additional clinic/account),
   * since platform rules allow only one subscription per product/package type.
   * If there are existing subscriptions, special offerings ("+n" options) will be shown.
   */
  const checkActiveSubscriptionsCount = useCallback(async () => {
    try {
      const { count: subscriptionsCount } =
        await ActiveSubscriptionsCounter.getActiveSubscriptionsCount();

      /*
        Delete 'activeSubscriptionsCount' by setting it to '', then set it with the current count.
        This two-step process ensures the attribute is accurately updated.
      */
      await Purchases.setAttributes({
        activeSubscriptionsCount: ''
      });
      await Purchases.setAttributes({
        activeSubscriptionsCount: subscriptionsCount.toString()
      });
    } catch {
      handleError('Error checking global subscriptions count.');
    }
  }, []);

  const login = useCallback(
    async (userId: string) => {
      if (platform === PLATFORMS.WEB || !connected || !isConfigured) {
        setLoading(false);
        setLoaded(true);
        return;
      }

      try {
        setLoading(true);
        setLoaded(false);
        await Purchases.logIn({ appUserID: userId });
        await checkSubscriptionStatus();
        await checkActiveSubscriptionsCount();
      } catch {
        handleError('Error logging in to RevenueCat.');
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    },
    [
      connected,
      platform,
      isConfigured,
      checkSubscriptionStatus,
      checkActiveSubscriptionsCount
    ]
  );

  const logout = useCallback(async () => {
    if (platform === PLATFORMS.WEB || !connected || !isConfigured) {
      return;
    }

    try {
      const isAnonymous = await Purchases.isAnonymous();
      if (!isAnonymous) {
        await Purchases.logOut();
      }
      setIsSubscribed(undefined);
      setEntitlements({
        all: {},
        active: {},
        verification: 'NOT_REQUESTED' as VERIFICATION_RESULT
      });
    } catch {
      handleError('Error logging out from RevenueCat.');
    }
  }, [connected, platform, isConfigured]);

  const fetchPlans = useCallback(async () => {
    if (platform === PLATFORMS.WEB || !connected || !isConfigured) {
      return;
    }

    try {
      await Purchases.syncAttributesAndOfferingsIfNeeded();
      const offerings = await Purchases.getOfferings();
      const currentOffering = offerings.current;
      if (
        currentOffering !== null &&
        currentOffering.availablePackages.length > 0
      ) {
        const fetchedPlans = currentOffering.availablePackages
          .sort((a, b) => a.product.price - b.product.price) // Sort by price
          .filter((plan) => PLAN_LIMITS[plan.identifier] >= dentists.length); // Filter by plan limit

        setPlans(fetchedPlans);
        setSelectedPlan(fetchedPlans[0]);
      }
    } catch {
      handleError('Error fetching plans.');
    }
  }, [connected, platform, isConfigured, dentists.length]);

  const purchase = useCallback(async () => {
    if (platform === PLATFORMS.WEB || !connected || !isConfigured) {
      return;
    }

    if (!selectedPlan) {
      displayToast({
        message: 'Please select a plan to continue.',
        position: 'top'
      });
      return;
    }

    try {
      setPurchasing(true);
      await Purchases.purchasePackage({ aPackage: selectedPlan });

      /**
       * Persist the assigned offering to ensure consistency across user sessions,
       * preventing issues caused by changes when some users' subscriptions expire.
       */
      const offeringIdentifier =
        selectedPlan.presentedOfferingContext?.offeringIdentifier;
      if (offeringIdentifier) {
        await Purchases.setAttributes({
          // RevenueCat targeting does not allow custom attribute values that start with '+'
          assignedOffering: offeringIdentifier.replace('+', '')
        });
      }
      /**
       * Edge Case: If multiple clinics share a single app store account,
       * both could end up with the same assignedOffering value when some of the subscriptions expire.
       * This can cause an endless loop of restorePurchases.
       * If this happens, manually resolve conflicting assignedOffering values in the RevenueCat dashboard.
       */
    } catch {
      handleError(
        'Subscription failed. Please try again or contact support if the issue persists.'
      );
    } finally {
      setPurchasing(false);
    }
  }, [connected, platform, selectedPlan, isConfigured]);

  const restore = useCallback(async () => {
    if (platform === PLATFORMS.WEB || !connected || !isConfigured) {
      return;
    }

    try {
      setPurchasing(true);
      await Purchases.restorePurchases();
    } catch {
      handleError('Error restoring purchases. Please contact support.');
    } finally {
      setPurchasing(false);
    }
  }, [connected, platform, isConfigured]);

  const manage = useCallback(async () => {
    if (platform === PLATFORMS.WEB || !connected || !isConfigured) {
      return;
    }

    try {
      await RevenueCatUI.presentCustomerCenter();
    } catch {
      handleError('Error opening customer center.');
    }
  }, [connected, platform, isConfigured]);

  const entitlement = useMemo(() => {
    if (
      Object.keys(entitlements.active).length === 0 ||
      !isConfigured ||
      platform === PLATFORMS.WEB
    ) {
      return undefined;
    }

    if (Object.keys(entitlements.active).length === 1) {
      return Object.values(entitlements.active)[0];
    }

    return Object.values(entitlements.active).reduce(
      (
        latest: PurchasesEntitlementInfo,
        entitlement: PurchasesEntitlementInfo
      ) => {
        if (!entitlement || !latest) {
          return Object.values(entitlements.active)[0];
        }

        return isAfter(
          parseDate(entitlement.latestPurchaseDate),
          parseDate(latest.latestPurchaseDate)
        )
          ? entitlement
          : latest;
      },
      Object.values(entitlements.active)[0]
    );
  }, [
    Object.keys(entitlements.active).length,
    Object.values(entitlements.active),
    isConfigured,
    platform
  ]);

  const profileCountLimit = useMemo(() => {
    if (
      plans.length === 0 ||
      !isConfigured ||
      platform === PLATFORMS.WEB ||
      !entitlement
    ) {
      return undefined;
    }

    if (entitlement.identifier === ENTITLEMENT_IDENTIFIERS.SOLO) {
      return PROFILE_COUNT_LIMITS.SOLO;
    }

    if (entitlement.identifier === ENTITLEMENT_IDENTIFIERS.TEAM) {
      return PROFILE_COUNT_LIMITS.TEAM;
    }

    if (entitlement.identifier === ENTITLEMENT_IDENTIFIERS.ENTERPRISE) {
      return PROFILE_COUNT_LIMITS.ENTERPRISE;
    }

    return PROFILE_COUNT_LIMITS.DEFAULT;
  }, [entitlement, plans, isConfigured, platform]);

  useEffect(() => {
    configure();
  }, [configure]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    checkEligibilityForTrial();
  }, [checkEligibilityForTrial]);

  useEffect(() => {
    if (!authenticated || isClinic === undefined) {
      return;
    }

    if (clinic?.id) {
      login(clinic.id);
      return;
    }

    if (isClinic === false) {
      logout();
      setLoading(false);
      setLoaded(true);
    }
  }, [authenticated, isClinic, clinic?.id, login, logout]);

  useEffect(() => {
    return () => {
      if (customerInfoListener) {
        Purchases.removeCustomerInfoUpdateListener({
          listenerToRemove: customerInfoListener
        });
      }
    };
  }, [customerInfoListener]);

  useEffect(() => {
    return () => {
      setIsSubscribed(undefined);
      setEntitlements({
        all: {},
        active: {},
        verification: 'NOT_REQUESTED' as VERIFICATION_RESULT
      });
      setIsEligibleForTrial(false);
      setIsConfigured(false);
      setPlans([]);
      setSelectedPlan(undefined);
      setLoading(false);
      setLoaded(false);
      setPurchasing(false);
    };
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        loading,
        loaded,
        purchasing,
        isSubscribed,
        checkEligibilityForTrial,
        isEligibleForTrial,
        login,
        logout,
        plans,
        selectedPlan,
        setSelectedPlan,
        purchase,
        restore,
        profileCountLimit,
        entitlement,
        manage
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider'
    );
  }
  return context;
};
